import { Box, Typography, Paper, InputAdornment, OutlinedInput, CircularProgress, IconButton, Button, Alert, Divider } from '@mui/material';
import BackButton from '../../components/Button/BackButton';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import LockIcon from '@mui/icons-material/Lock';
import { useUsuarioStore } from '../../store/usuarioStore';
import { useAuth0 } from '@auth0/auth0-react';
import { useState, useEffect } from 'react';

export const User = () => {
  const { usuario, editarUsuario, cambiarContrasena } = useUsuarioStore();
  const { user: auth0User } = useAuth0();
  const isLoading = !usuario;
  
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: ''
  });
  const [isSaving, setIsSaving] = useState(false);

  // Detectar si el usuario está autenticado con Google
  const isGoogleUser = auth0User?.sub?.startsWith('google-oauth2|');
  const canEdit = !isGoogleUser;

  useEffect(() => {
    if (usuario && !isEditing) {
      setFormData({
        nombre: usuario.nombre,
        apellido: usuario.apellido
      });
    }
  }, [usuario, isEditing]);

  const handleEdit = () => {
    if (!canEdit) return;
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (usuario) {
      setFormData({
        nombre: usuario.nombre,
        apellido: usuario.apellido
      });
    }
  };

  const handleSave = async () => {
    if (!usuario || !canEdit) return;
    
    setIsSaving(true);
    try {
      await editarUsuario(formData);
      setIsEditing(false);
    } catch (error) {
      console.error('Error al guardar:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleCambiarContrasena = async () => {
    try {
      await cambiarContrasena();
    } catch (error) {
      console.error('Error al solicitar cambio de contraseña:', error);
    }
  };

  return (
    <Box sx={{ 
      p: { xs: 2, sm: 3 }, 
      maxWidth: '1200px', 
      margin: '0 auto',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Box sx={{ 
        mb: { xs: 2, sm: 4 }, 
        display: 'flex', 
        justifyContent: 'flex-end'
      }}>
        <BackButton to="/" label="Volver al listado" />
      </Box>

      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 2, sm: 4 }, 
          border: '1px solid #E6E6E6',
          borderRadius: 2,
          backgroundColor: '#fff',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: { xs: 3, sm: 4 },
          textAlign: { xs: 'center', sm: 'left' },
          width: '100%',
          maxWidth: '500px'
        }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center'
          }}>
            {isLoading ? (
              <CircularProgress size={40} sx={{ color: '#666' }} />
            ) : (
              <AccountCircleIcon sx={{ 
                fontSize: { xs: 60, sm: 40 }, 
                color: '#666', 
                mr: { xs: 0, sm: 2 },
                mb: { xs: 2, sm: 0 }
              }} />
            )}
            <Box>
              <Typography variant="h5" sx={{ 
                fontWeight: 500, 
                color: '#333',
                fontSize: { xs: '1.5rem', sm: '1.25rem' }
              }}>
                Información del Usuario
              </Typography>
              <Typography variant="body2" sx={{ 
                color: '#666', 
                mt: 0.5,
                fontSize: { xs: '1rem', sm: '0.875rem' }
              }}>
                Gestiona tu información personal
              </Typography>
            </Box>
          </Box>
          
          {!isLoading && !isEditing && canEdit && (
            <IconButton
              onClick={handleEdit}
              sx={{
                color: '#1976d2',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.04)'
                }
              }}
            >
              <EditIcon />
            </IconButton>
          )}
        </Box>

        {!isLoading && isGoogleUser && (
          <Alert 
            severity="info" 
            sx={{ 
              mb: 3, 
              width: '100%', 
              maxWidth: '500px',
              '& .MuiAlert-message': {
                fontSize: { xs: '14px', sm: '16px' }
              }
            }}
          >
            Los usuarios autenticados con Google no pueden editar su información personal. 
            Los datos se sincronizan automáticamente desde tu cuenta de Google.
          </Alert>
        )}

        <Box
          component="form"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 4, sm: 3 },
            width: '100%',
            maxWidth: '500px',
            position: 'relative'
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ 
              mb: 1.5, 
              color: '#666',
              textAlign: 'left',
              fontSize: { xs: '1rem', sm: '0.875rem' },
              fontWeight: 500,
              pl: 1
            }}>
              Nombre
            </Typography>
            <OutlinedInput
              placeholder="Tu nombre"
              value={isLoading ? 'Cargando...' : isEditing ? formData.nombre : usuario?.nombre || ''}
              onChange={handleChange('nombre')}
              fullWidth
              disabled={!isEditing || !canEdit}
              startAdornment={
                <InputAdornment position="start">
                  {isLoading ? (
                    <CircularProgress size={20} sx={{ color: '#666' }} />
                  ) : (
                    <PersonIcon sx={{ color: '#666', fontSize: { xs: 24, sm: 20 } }} />
                  )}
                </InputAdornment>
              }
              sx={{
                backgroundColor: isEditing && canEdit ? '#fff' : '#f8f9fa',
                height: { xs: '56px', sm: '48px' },
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: isEditing && canEdit ? '#fff' : '#f3f4f6'
                },
                '& fieldset': {
                  borderColor: isEditing && canEdit ? '#1976d2' : '#e0e0e0'
                },
                '& input': {
                  textAlign: 'center',
                  fontSize: { xs: '1.1rem', sm: '1rem' },
                  fontWeight: 500,
                  cursor: isEditing && canEdit ? 'text' : 'default'
                }
              }}
            />
          </Box>
          
          <Box>
            <Typography variant="subtitle2" sx={{ 
              mb: 1.5, 
              color: '#666',
              textAlign: 'left',
              fontSize: { xs: '1rem', sm: '0.875rem' },
              fontWeight: 500,
              pl: 1
            }}>
              Apellido
            </Typography>
            <OutlinedInput
              placeholder="Tu apellido"
              value={isLoading ? 'Cargando...' : isEditing ? formData.apellido : usuario?.apellido || ''}
              onChange={handleChange('apellido')}
              fullWidth
              disabled={!isEditing || !canEdit}
              startAdornment={
                <InputAdornment position="start">
                  {isLoading ? (
                    <CircularProgress size={20} sx={{ color: '#666' }} />
                  ) : (
                    <PersonIcon sx={{ color: '#666', fontSize: { xs: 24, sm: 20 } }} />
                  )}
                </InputAdornment>
              }
              sx={{
                backgroundColor: isEditing && canEdit ? '#fff' : '#f8f9fa',
                height: { xs: '56px', sm: '48px' },
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: isEditing && canEdit ? '#fff' : '#f3f4f6'
                },
                '& fieldset': {
                  borderColor: isEditing && canEdit ? '#1976d2' : '#e0e0e0'
                },
                '& input': {
                  textAlign: 'center',
                  fontSize: { xs: '1.1rem', sm: '1rem' },
                  fontWeight: 500,
                  cursor: isEditing && canEdit ? 'text' : 'default'
                }
              }}
            />
          </Box>
          
          <Box>
            <Typography variant="subtitle2" sx={{ 
              mb: 1.5, 
              color: '#666',
              textAlign: 'left',
              fontSize: { xs: '1rem', sm: '0.875rem' },
              fontWeight: 500,
              pl: 1
            }}>
              Email
            </Typography>
            <OutlinedInput
              placeholder="Tu email"
              value={isLoading ? 'Cargando...' : usuario?.email || ''}
              fullWidth
              disabled={true}
              startAdornment={
                <InputAdornment position="start">
                  {isLoading ? (
                    <CircularProgress size={20} sx={{ color: '#666' }} />
                  ) : (
                    <EmailIcon sx={{ color: '#666', fontSize: { xs: 24, sm: 20 } }} />
                  )}
                </InputAdornment>
              }
              sx={{
                backgroundColor: '#f8f9fa',
                height: { xs: '56px', sm: '48px' },
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: '#f3f4f6'
                },
                '& fieldset': {
                  borderColor: '#e0e0e0'
                },
                '& input': {
                  textAlign: 'center',
                  fontSize: { xs: '1.1rem', sm: '1rem' },
                  fontWeight: 500,
                  cursor: 'default'
                }
              }}
            />
          </Box>

          {isEditing && (
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              justifyContent: 'center',
              mt: 2
            }}>
              <Button
                onClick={handleCancel}
                variant="outlined"
                disabled={isSaving}
                sx={{ 
                  textTransform: 'none',
                  minWidth: '100px'
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                disabled={isSaving}
                startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
                sx={{ 
                  textTransform: 'none',
                  minWidth: '100px'
                }}
              >
                {isSaving ? 'Guardando...' : 'Guardar'}
              </Button>
            </Box>
          )}
        </Box>

        {/* Sección de seguridad */}
        {!isLoading && !isGoogleUser && (
          <>
            <Divider sx={{ my: 3, width: '100%', maxWidth: '500px' }} />
            
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: 2,
              width: '100%',
              maxWidth: '500px'
            }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 600,
                color: '#333',
                fontSize: { xs: '1.1rem', sm: '1.25rem' }
              }}>
                Seguridad
              </Typography>
              
              <Button
                variant="outlined"
                startIcon={<LockIcon />}
                onClick={handleCambiarContrasena}
                disabled={isLoading}
                sx={{
                  textTransform: 'none',
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  '&:hover': {
                    borderColor: '#1565c0',
                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                  },
                  fontSize: { xs: '14px', sm: '16px' },
                  py: 1.5
                }}
              >
                Cambiar contraseña
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default User; 